import { type KeyboardEvent, type ReactElement, useEffect, useMemo, useRef, useState } from 'react';
import { FILTER_LIMITS, type FilterOp, type HavingOp, getDimension } from '@vello/analytics-semantic';
import { type AnalyticsBiCatalogView, type AnalyticsBiValuesView } from '@vello/api-client';
import { Badge, Button, CheckRow, Field, FormGrid, IconButton, Notice, SearchInput, SegmentedControl, Select, TextArea, TextInput, Stack, Row, Text, Grid, Box, ChipFrame, ChipMain } from '@vello/web-theme';
import {
  type BiFilterSpec,
  type BiFilterState,
  type BiHavingSpec,
  type ConditionAt,
  addCondition,
  addHaving,
  biErrorView,
  canAddCondition,
  defaultOp,
  isFilled,
  isHavingFilled,
  operatorsOf,
  opKind,
  parseBulkValues,
  removeCondition,
  removeHaving,
  replaceCondition,
  replaceHaving,
  sharedBiQueue,
  toggledOp,
} from '../../../src/index';
import { useAdmin } from '../../session';
import { type BiText } from './bi-ui';

/** Why a dimension cannot be filtered on here: the SERVER's reason code (and the choice that causes it), or a panel's own. */
export interface FilterRefusal {
  readonly code: string;
  readonly because?: string;
}

export interface FilterBuilderProps {
  readonly idPrefix: string;
  readonly catalog: AnalyticsBiCatalogView;
  readonly text: BiText;
  readonly value: BiFilterState;
  readonly onChange: (next: BiFilterState) => void;
  /** A dimension this screen cannot filter by, with the reason; shown disabled. */
  readonly refuse?: (dimensionId: string) => FilterRefusal | undefined;
  /** The dimensions to list, in order (default: the catalog's). A dimension the catalog does not list for this caller (a person without the word) can be listed here, disabled. */
  readonly dimensionIds?: readonly string[];
  /** The measures a HAVING condition may be about; absent = this screen has no HAVING (the panels' bar). */
  readonly havingMetrics?: readonly string[];
}

const NULL_OPS: readonly FilterOp[] = ['is_null', 'not_null'];
const HAVING_OP_LIST: readonly HavingOp[] = ['gt', 'gte', 'lt', 'lte', 'eq', 'between'];
const HOURS = Array.from({ length: 24 }, (_, h) => String(h).padStart(2, '0'));
const WEEKDAYS = ['1', '2', '3', '4', '5', '6', '7'];

/** The reason text of a refusal, in words (the code stays out of the screen; support reads it from the server's problem document). */
export function refusalWords(text: BiText, r: FilterRefusal): string {
  return (() => {
    switch (r.code) {
      case 'ANALYTICS_PERSONAL_FORBIDDEN':
        return text.t('bi.refused.personal');
      case 'ANALYTICS_LINKAGE_FORBIDDEN':
        return text.t('bi.refused.linkage');
      case 'DIMENSION_NOT_AVAILABLE_FOR_METRICS':
        return text.t('bi.refused.unavailableDim', { other: text.metricName(r.because ?? '') });
      case 'PANEL_PINS_DIMENSION':
        return text.t('bi.flt.refused.pinned');
      case 'PANEL_NO_PEOPLE':
        return text.t('bi.flt.refused.people');
      default:
        return text.t('bi.flt.refused.notAvailable');
    }
  })();
}

// ───────────────────────────── words for a condition ─────────────────────────────

function unitOf(valueType: string): string {
  switch (valueType) {
    case 'distance':
      return ' m';
    case 'duration':
      return ' s';
    default:
      return '';
  }
}

/** A condition in a sentence: "Store contains “pizza”", "Distance between 2000 and 9000 m", "Hour of day between 22:00 and 05:00". */
export function describeCondition(text: BiText, catalog: AnalyticsBiCatalogView, spec: BiFilterSpec): string {
  const { t, v } = text;
  const def = catalog.dimensions.find((d) => d.id === spec.dimension);
  const type = def?.valueType ?? getDimension(spec.dimension)?.valueType ?? 'ref';
  const name = text.dimName(spec.dimension);
  const op = v('analytics.filter_op', spec.op);
  const kind = opKind(spec.op);
  const value = (val: string): string => (type === 'market' ? text.marketName(val) : type === 'hour' && kind !== 'set' ? `${val.padStart(2, '0')}:00` : type === 'weekday' && kind !== 'set' ? weekdayName(text, val) : type === 'flag' ? t(`bi.flt.flag.${val}`) : text.valueText(spec.dimension, val));
  const cur = spec.currency !== undefined && spec.currency !== '' ? ` ${spec.currency}` : '';
  switch (kind) {
    case 'none':
      return `${name} ${op}`;
    case 'text':
      return `${name} ${op} “${spec.values[0] ?? ''}”`;
    case 'set':
      return `${name} ${op} ${spec.values.map(value).join(', ')}`;
    case 'one':
      return `${name} ${op} ${value(spec.values[0] ?? '')}${unitOf(type)}${cur}`;
    case 'between':
      return `${name} ${op} ${value(spec.values[0] ?? '')} ${t('bi.flt.and')} ${value(spec.values[1] ?? '')}${unitOf(type)}${cur}`;
  }
}

export function describeHaving(text: BiText, spec: BiHavingSpec): string {
  const { t, v } = text;
  const cur = spec.currency !== undefined && spec.currency !== '' ? ` ${spec.currency}` : '';
  const vals = spec.op === 'between' ? `${spec.values[0] ?? ''} ${t('bi.flt.and')} ${spec.values[1] ?? ''}` : (spec.values[0] ?? '');
  return `${text.metricName(spec.metric)} ${v('analytics.filter_op', spec.op)} ${vals}${cur}`;
}

function weekdayName(text: BiText, v: string): string {
  const d = new Date(Date.UTC(2024, 0, Number(v)));
  return new Intl.DateTimeFormat(text.lang, { weekday: 'long', timeZone: 'UTC' }).format(d);
}

// ───────────────────────────── the values of a dimension (search) ─────────────────────────────

type ValuesState = { readonly status: 'idle' } | { readonly status: 'loading' } | { readonly status: 'ready'; readonly view: AnalyticsBiValuesView } | { readonly status: 'error'; readonly messageId: string };

/** The distinct values of a dimension for the multi-select: a new search is a new request (debounced), a late answer is dropped, nothing is cached. */
function useDimensionValues(dimension: string, search: string, enabled: boolean): ValuesState {
  const { runtime } = useAdmin();
  const [state, setState] = useState<ValuesState>({ status: 'idle' });
  useEffect(() => {
    if (!enabled) {
      setState({ status: 'idle' });
      return undefined;
    }
    let live = true;
    setState({ status: 'loading' });
    const timer = setTimeout(() => {
      void sharedBiQueue
        .run(() => runtime.analyticsBi.values({ dimension, ...(search !== '' ? { search } : {}), limit: 20 }), () => !live)
        .then((res) => {
          if (!live || res === undefined) return;
          setState(res.ok ? { status: 'ready', view: res.value } : { status: 'error', messageId: biErrorView(res.error).messageId });
        });
    }, search === '' ? 0 : 250);
    return () => {
      live = false;
      clearTimeout(timer);
    };
  }, [runtime, dimension, search, enabled]);
  return state;
}

/** The values a closed, small dimension needs no request for. */
function staticValues(text: BiText, dimension: string): readonly { value: string; label: string }[] | undefined {
  switch (dimension) {
    case 'hour':
      return HOURS.map((h) => ({ value: h, label: `${h}:00` }));
    case 'weekday':
      return WEEKDAYS.map((d) => ({ value: d, label: weekdayName(text, d) }));
    case 'holiday':
      return ['true', 'false'].map((flag) => ({ value: flag, label: text.t(`bi.flt.flag.${flag}`) }));
    default:
      return undefined;
  }
}

function ValuePicker({ id, dimension, enumerable, text, selected, single, onChange }: { id: string; dimension: string; enumerable: boolean; text: BiText; selected: readonly string[]; single: boolean; onChange: (v: readonly string[]) => void }): ReactElement {
  const { t } = text;
  const [search, setSearch] = useState('');
  const [paste, setPaste] = useState('');
  const [note, setNote] = useState<string | undefined>(undefined);
  const fixed = staticValues(text, dimension);
  const values = useDimensionValues(dimension, search, enumerable && fixed === undefined);
  const toggle = (v: string): void => {
    if (selected.includes(v)) onChange(selected.filter((x) => x !== v));
    else if (single) onChange([v]);
    else if (selected.length < FILTER_LIMITS.maxValues) onChange([...selected, v]);
  };
  const options: readonly { value: string; label: string; count?: number | null }[] = fixed ?? (values.status === 'ready' ? values.view.items.map((i) => ({ value: i.key, label: text.valueText(dimension, i.key, i.label), count: i.count })) : []);

  const addPasted = (): void => {
    const parsed = parseBulkValues(paste, single ? [] : selected);
    onChange(single ? parsed.values.slice(0, 1) : parsed.values);
    setPaste('');
    setNote(parsed.overflow > 0 ? t('bi.flt.paste.overflow', { max: FILTER_LIMITS.maxValues, dropped: parsed.overflow }) : parsed.duplicates > 0 ? t('bi.flt.paste.duplicates', { n: parsed.duplicates }) : undefined);
  };

  return (
    <Stack gap={3}>
      {selected.length > 0 && (
        <Row as="ul" gap={2} align="stretch" wrap aria-label={t('bi.flt.selected')}>
          {selected.map((v) => (
            <li key={v} className="vello-admin-a-inline-cell">
              <Badge tone="neutral">{text.valueText(dimension, v)}</Badge>
              <IconButton icon="x" label={t('bi.flt.removeValue', { value: text.valueText(dimension, v) })} onClick={() => toggle(v)} />
            </li>
          ))}
        </Row>
      )}
      {(fixed !== undefined || enumerable) && (
        <>
          {fixed === undefined && <SearchInput id={`${id}-search`} value={search} onChange={setSearch} onClear={() => setSearch('')} placeholder={t('bi.flt.search')} clearLabel={t('bi.flt.search.clear')} />}
          {values.status === 'error' && <Notice tone="warning">{t(values.messageId)}</Notice>}
          {values.status === 'loading' && fixed === undefined && <Text role="caption" tone="secondary">{t('ui.loading')}</Text>}
          {values.status === 'ready' && options.length === 0 && <Text role="caption" tone="secondary">{t('bi.flt.noValues')}</Text>}
          <Grid min="md" gap={1}>
            {options.map((o) => (
              <CheckRow key={o.value} id={`${id}-v-${o.value}`} checked={selected.includes(o.value)} onChange={() => toggle(o.value)}>
                {o.label}
                {o.count !== null && o.count !== undefined ? <Text role="caption" tone="secondary">{` · ${text.fmt.count(o.count)}`}</Text> : null}
              </CheckRow>
            ))}
          </Grid>
          {values.status === 'ready' && values.view.truncated && <Text role="caption" tone="secondary">{t('bi.flt.truncated')}</Text>}
        </>
      )}
      {!single && (
        <Stack gap={2}>
          <Field label={t('bi.flt.paste')} htmlFor={`${id}-paste`} hint={t('bi.flt.paste.hint', { max: FILTER_LIMITS.maxValues })}>
            <TextArea id={`${id}-paste`} value={paste} rows={3} onChange={(e) => setPaste(e.target.value)} />
          </Field>
          <div>
            <Button variant="secondary" disabled={paste.trim() === ''} onClick={addPasted}>
              {t('bi.flt.paste.add')}
            </Button>
          </div>
          {note !== undefined && <Notice tone="warning">{note}</Notice>}
        </Stack>
      )}
      {single && !enumerable && fixed === undefined && (
        <Field label={t('bi.flt.value')} htmlFor={`${id}-one`}>
          <TextInput id={`${id}-one`} value={selected[0] ?? ''} onChange={(e) => onChange(e.target.value === '' ? [] : [e.target.value])} />
        </Field>
      )}
    </Stack>
  );
}

// ───────────────────────────── the editor of one condition ─────────────────────────────

interface Draft {
  readonly dimension: string;
  readonly op: FilterOp;
  readonly values: readonly string[];
  readonly currency: string;
  /** Where it goes when added: the AND list (undefined) or an OR group (index; `groups` = a new one). Editing keeps the place. */
  readonly group: number | undefined;
}

function ConditionEditor({
  idPrefix,
  catalog,
  text,
  initial,
  editing,
  groups,
  allowJoin,
  refuse,
  dimensionIds,
  onApply,
  onCancel,
}: {
  idPrefix: string;
  catalog: AnalyticsBiCatalogView;
  text: BiText;
  initial: Draft;
  editing: boolean;
  groups: number;
  allowJoin: boolean;
  refuse: ((dimensionId: string) => FilterRefusal | undefined) | undefined;
  dimensionIds: readonly string[];
  onApply: (draft: Draft, spec: BiFilterSpec) => void;
  onCancel: () => void;
}): ReactElement {
  const { t, v } = text;
  const [d, setD] = useState<Draft>(initial);
  const def = catalog.dimensions.find((x) => x.id === d.dimension);
  const personal = def?.personal === true;
  // A person is filtered by "has / has no", never by a key: the link, the saved sets and the report refuse a key.
  const ops = personal ? operatorsOf(catalog, d.dimension).filter((o) => NULL_OPS.includes(o)) : operatorsOf(catalog, d.dimension);
  const kind = opKind(d.op);
  const money = def?.valueType === 'money';
  const currencies = [...new Set(catalog.markets.map((m) => m.currency))].sort();
  const spec: BiFilterSpec = { dimension: d.dimension, op: d.op, values: d.values, ...(money && kind !== 'none' && d.currency !== '' ? { currency: d.currency } : {}) };
  const ready = isFilled(spec, money);
  const id = `${idPrefix}-edit`;
  const fixed = staticValues(text, d.dimension);
  const ordinal = def?.valueType === 'hour' || def?.valueType === 'weekday';

  const setDimension = (dimension: string): void => {
    const nextOps = operatorsOf(catalog, dimension);
    const personalNext = catalog.dimensions.find((x) => x.id === dimension)?.personal === true;
    const usable = personalNext ? nextOps.filter((o) => NULL_OPS.includes(o)) : nextOps;
    setD({ ...d, dimension, op: defaultOp(usable), values: [], currency: currencies[0] ?? '' });
  };

  const bound = (index: number, label: string): ReactElement => {
    const current = d.values[index] ?? '';
    const set = (v: string): void => {
      const next = [...d.values];
      next[index] = v;
      setD({ ...d, values: next });
    };
    const fid = `${id}-bound-${index}`;
    return (
      <Field label={label} htmlFor={fid}>
        {fixed !== undefined && ordinal ? (
          <Select id={fid} value={current} onChange={(e) => set(e.target.value)}>
            <option value="">{t('bi.filter.choose')}</option>
            {fixed.map((o) => (
              <option key={o.value} value={String(Number(o.value))}>
                {o.label}
              </option>
            ))}
          </Select>
        ) : (
          <TextInput id={fid} inputMode="numeric" value={current} onChange={(e) => set(e.target.value)} />
        )}
      </Field>
    );
  };

  return (
    <Box role="group" aria-label={t('bi.flt.editor')} gap={4}>
      <FormGrid min="md">
        <Field label={t('bi.filter.dimension')} htmlFor={`${id}-dim`}>
          <Select id={`${id}-dim`} value={d.dimension} onChange={(e) => setDimension(e.target.value)} disabled={editing}>
            {dimensionIds.map((id) => {
              const why = refuse?.(id);
              const personal = (catalog.dimensions.find((x) => x.id === id)?.personal ?? getDimension(id)?.personal) === true;
              return (
                <option key={id} value={id} disabled={why !== undefined && id !== d.dimension}>
                  {text.dimName(id)}
                  {personal ? ` · ${t('bi.personal.tag')}` : ''}
                  {why !== undefined ? ` — ${refusalWords(text, why)}` : ''}
                </option>
              );
            })}
          </Select>
        </Field>
        <Field label={t('bi.filter.op')} htmlFor={`${id}-op`}>
          <Select id={`${id}-op`} value={d.op} onChange={(e) => setD({ ...d, op: e.target.value as FilterOp, values: opKind(e.target.value as FilterOp) === 'set' && kind === 'set' ? d.values : [] })}>
            {ops.map((o) => (
              <option key={o} value={o}>
                {v('analytics.filter_op', o)}
              </option>
            ))}
          </Select>
        </Field>
        {allowJoin && !editing && (
          <Field label={t('bi.flt.join')} htmlFor={`${id}-join`}>
            <Select id={`${id}-join`} value={d.group === undefined ? 'and' : String(d.group)} onChange={(e) => setD({ ...d, group: e.target.value === 'and' ? undefined : Number(e.target.value) })}>
              <option value="and">{t('bi.flt.join.and')}</option>
              {Array.from({ length: groups }, (_, g) => (
                <option key={g} value={g}>
                  {t('bi.flt.join.group', { n: g + 1 })}
                </option>
              ))}
              <option value={groups}>{t('bi.flt.join.new')}</option>
            </Select>
          </Field>
        )}
      </FormGrid>

      {(d.op === 'in' || d.op === 'not_in') && toggledOp(d.op) !== undefined && (
        <SegmentedControl<'in' | 'not_in'>
          ariaLabel={t('bi.flt.includeExclude')}
          value={d.op}
          onChange={(op) => setD({ ...d, op })}
          options={[
            { id: 'in', label: t('bi.flt.include') },
            { id: 'not_in', label: t('bi.flt.exclude') },
          ]}
        />
      )}

      {kind === 'set' && <ValuePicker id={id} dimension={d.dimension} enumerable={def?.enumerable === true} text={text} selected={d.values} single={d.op === 'eq'} onChange={(values) => setD({ ...d, values })} />}
      {kind === 'text' && (
        <Field label={t('bi.flt.text')} htmlFor={`${id}-text`} hint={t('bi.flt.text.hint')}>
          <TextInput id={`${id}-text`} value={d.values[0] ?? ''} maxLength={FILTER_LIMITS.maxTextLength} onChange={(e) => setD({ ...d, values: e.target.value === '' ? [] : [e.target.value] })} />
        </Field>
      )}
      {kind === 'one' && <FormGrid min="sm">{bound(0, t('bi.flt.value'))}</FormGrid>}
      {kind === 'between' && (
        <FormGrid min="sm">
          {bound(0, t('bi.flt.from'))}
          {bound(1, t('bi.flt.to'))}
        </FormGrid>
      )}
      {(kind === 'one' || kind === 'between') && def?.valueType === 'hour' && d.op === 'between' && <Text role="caption" tone="secondary">{t('bi.flt.wrap')}</Text>}
      {(kind === 'one' || kind === 'between') && !ordinal && <Text role="caption" tone="secondary">{t(`bi.flt.unit.${def?.valueType ?? 'count'}`)}</Text>}
      {money && kind !== 'none' && (
        <Field label={t('bi.flt.currency')} htmlFor={`${id}-currency`}>
          <Select id={`${id}-currency`} value={d.currency} onChange={(e) => setD({ ...d, currency: e.target.value })}>
            {currencies.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </Select>
        </Field>
      )}

      <Row gap={3} align="stretch" wrap>
        <Button variant="primary" disabled={!ready} onClick={() => onApply(d, spec)}>
          {editing ? t('bi.flt.update') : t('bi.flt.addCondition')}
        </Button>
        <Button variant="ghost" onClick={onCancel}>
          {t('ui.cancel')}
        </Button>
      </Row>
    </Box>
  );
}

// ───────────────────────────── HAVING ─────────────────────────────

function HavingEditor({ idPrefix, catalog, text, metrics, initial, editing, onApply, onCancel }: { idPrefix: string; catalog: AnalyticsBiCatalogView; text: BiText; metrics: readonly string[]; initial: BiHavingSpec; editing: boolean; onApply: (s: BiHavingSpec) => void; onCancel: () => void }): ReactElement {
  const { t, v } = text;
  const [h, setH] = useState<BiHavingSpec>(initial);
  const unit = catalog.metrics.find((m) => m.id === h.metric)?.unit;
  const money = unit === 'money';
  const currencies = [...new Set(catalog.markets.map((m) => m.currency))].sort();
  const clean: BiHavingSpec = money ? { metric: h.metric, op: h.op, values: h.values, currency: h.currency ?? currencies[0] ?? '' } : { metric: h.metric, op: h.op, values: h.values };
  const id = `${idPrefix}-having`;
  const count = h.op === 'between' ? 2 : 1;
  const set = (i: number, v: string): void => {
    const next = [...h.values];
    next[i] = v;
    setH({ ...h, values: next.slice(0, count) });
  };
  return (
    <Box role="group" aria-label={t('bi.having.editor')} gap={4}>
      <FormGrid min="md">
        <Field label={t('bi.having.metric')} htmlFor={`${id}-metric`}>
          <Select id={`${id}-metric`} value={h.metric} onChange={(e) => setH({ ...h, metric: e.target.value, values: [] })}>
            {metrics.map((m) => (
              <option key={m} value={m}>
                {text.metricName(m)}
              </option>
            ))}
          </Select>
        </Field>
        <Field label={t('bi.filter.op')} htmlFor={`${id}-op`}>
          <Select id={`${id}-op`} value={h.op} onChange={(e) => setH({ ...h, op: e.target.value as HavingOp, values: h.values.slice(0, e.target.value === 'between' ? 2 : 1) })}>
            {HAVING_OP_LIST.map((o) => (
              <option key={o} value={o}>
                {v('analytics.filter_op', o)}
              </option>
            ))}
          </Select>
        </Field>
        <Field label={h.op === 'between' ? t('bi.flt.from') : t('bi.flt.value')} htmlFor={`${id}-v0`} hint={t(unit === 'ratio' ? 'bi.having.hint.ratio' : unit === 'money' ? 'bi.having.hint.money' : 'bi.having.hint.number')}>
          <TextInput id={`${id}-v0`} inputMode="decimal" value={h.values[0] ?? ''} onChange={(e) => set(0, e.target.value)} />
        </Field>
        {h.op === 'between' && (
          <Field label={t('bi.flt.to')} htmlFor={`${id}-v1`}>
            <TextInput id={`${id}-v1`} inputMode="decimal" value={h.values[1] ?? ''} onChange={(e) => set(1, e.target.value)} />
          </Field>
        )}
        {money && (
          <Field label={t('bi.flt.currency')} htmlFor={`${id}-currency`}>
            <Select id={`${id}-currency`} value={h.currency ?? currencies[0] ?? ''} onChange={(e) => setH({ ...h, currency: e.target.value })}>
              {currencies.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </Select>
          </Field>
        )}
      </FormGrid>
      <Row gap={3} align="stretch" wrap>
        <Button variant="primary" disabled={!isHavingFilled(clean, money)} onClick={() => onApply(clean)}>
          {editing ? t('bi.flt.update') : t('bi.flt.addCondition')}
        </Button>
        <Button variant="ghost" onClick={onCancel}>
          {t('ui.cancel')}
        </Button>
      </Row>
    </Box>
  );
}

// ───────────────────────────── the builder ─────────────────────────────

/** One active condition: its main button opens the editor, a second 48 px control removes it. (Defined outside the builder so a render never remounts it and focus stays.) */
function Chip({ t, label, main, onEdit, onRemove }: { t: BiText['t']; label: string; main: string; onEdit: () => void; onRemove: () => void }): ReactElement {
  return (
    <ChipFrame>
      <ChipMain
        data-chip-main={main}
        onClick={onEdit}
        title={t('bi.flt.edit')}
      >
        {label}
      </ChipMain>
      <IconButton icon="x" label={t('bi.flt.removeFilter', { filter: label })} onClick={onRemove} />
    </ChipFrame>
  );
}

type Editing = { readonly kind: 'condition'; readonly at: ConditionAt | undefined; readonly draft: Draft } | { readonly kind: 'having'; readonly index: number | undefined; readonly spec: BiHavingSpec };

/**
 * The reusable filter builder of Análises (D528): the active conditions as chips (keyboard-navigable, each a 48 px target: the chip opens its
 * editor, a second control removes it), OR groups, HAVING on a measure, and the editor that adds or changes one. The Explorer and the global bar
 * of the panels are the same component; what differs is only what each may filter by (`refuse`) and whether HAVING exists (`havingMetrics`).
 */
export function FilterBuilder({ idPrefix, catalog, text, value, onChange, refuse, havingMetrics, dimensionIds }: FilterBuilderProps): ReactElement {
  const { t } = text;
  const listed = useMemo(() => dimensionIds ?? catalog.dimensions.map((d) => d.id), [dimensionIds, catalog]);
  const [editing, setEditing] = useState<Editing | undefined>(undefined);
  const list = useRef<HTMLDivElement>(null);
  const firstFree = useMemo(() => listed.find((id) => getDimension(id)?.personal !== true && refuse?.(id) === undefined) ?? listed[0] ?? 'market', [listed, refuse]);
  const currency = [...new Set(catalog.markets.map((m) => m.currency))].sort()[0] ?? '';

  const startAdd = (): void => {
    const dimension = firstFree;
    setEditing({ kind: 'condition', at: undefined, draft: { dimension, op: defaultOp(operatorsOf(catalog, dimension)), values: [], currency, group: undefined } });
  };
  const apply = (e: Extract<Editing, { kind: 'condition' }>, d: Draft, spec: BiFilterSpec): void => {
    onChange(e.at !== undefined ? replaceCondition(value, e.at, spec) : addCondition(value, spec, d.group));
    setEditing(undefined);
  };

  // Chips: ArrowLeft / ArrowRight move between the chips' buttons, Delete or Backspace removes the focused one.
  const onKey = (e: KeyboardEvent<HTMLDivElement>): void => {
    const target = e.target as HTMLElement;
    const chips = Array.from(list.current?.querySelectorAll<HTMLElement>('[data-chip-main]') ?? []);
    const at = chips.indexOf(target);
    if (at < 0) return;
    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') chips[Math.min(chips.length - 1, at + 1)]?.focus();
    else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') chips[Math.max(0, at - 1)]?.focus();
    else if (e.key === 'Home') chips[0]?.focus();
    else if (e.key === 'End') chips[chips.length - 1]?.focus();
    else if (e.key === 'Delete' || e.key === 'Backspace') {
      const raw = target.getAttribute('data-chip-main') ?? '';
      if (raw.startsWith('h')) onChange(removeHaving(value, Number(raw.slice(1))));
      else {
        const [g, i] = raw.split(':');
        onChange(removeCondition(value, { ...(g !== '' ? { group: Number(g) } : {}), index: Number(i) }));
      }
      e.preventDefault();
      chips[Math.max(0, at - 1)]?.focus();
    }
  };

  const total = value.filters.length + value.anyOf.reduce((n, g) => n + g.length, 0) + value.having.length;
  const dims = (spec: BiFilterSpec): string => describeCondition(text, catalog, spec);

  return (
    <Stack gap={3}>
      <Row gap={3} align="center" wrap>
        <Text role="caption" tone="secondary">{t('bi.filters')}</Text>
        <Button variant="secondary" disabled={!canAddCondition(value) || editing !== undefined} onClick={startAdd}>
          {t('bi.filter.add')}
        </Button>
        {havingMetrics !== undefined && (
          <Button
            variant="secondary"
            disabled={value.having.length >= FILTER_LIMITS.maxHaving || havingMetrics.length === 0 || editing !== undefined}
            onClick={() => setEditing({ kind: 'having', index: undefined, spec: { metric: havingMetrics[0] ?? 'orders', op: 'gt', values: [] } })}
          >
            {t('bi.having.add')}
          </Button>
        )}
        {total > 0 && (
          <Button variant="ghost" onClick={() => onChange({ filters: [], anyOf: [], having: [] })}>
            {t('bi.flt.clear')}
          </Button>
        )}
      </Row>

      <div ref={list} onKeyDown={onKey} className="vello-stack" data-gap="3">
        {value.filters.length > 0 && (
          <Row as="ul" gap={2} align="stretch" wrap aria-label={t('bi.flt.active')}>
            {value.filters.map((f, i) => (
              <Chip
                t={t}
                key={`f${i}`}
                main={`:${i}`}
                label={dims(f)}
                onEdit={() => setEditing({ kind: 'condition', at: { index: i }, draft: { dimension: f.dimension, op: f.op, values: f.values, currency: f.currency ?? currency, group: undefined } })}
                onRemove={() => onChange(removeCondition(value, { index: i }))}
              />
            ))}
          </Row>
        )}
        {value.anyOf.map((group, g) => (
          <Stack gap={2} key={`g${g}`}>
            <Text role="caption" tone="secondary">{g === 0 ? t('bi.flt.group.first', { n: g + 1 }) : t('bi.flt.group.or', { n: g + 1 })}</Text>
            <Row as="ul" gap={2} align="stretch" wrap aria-label={t('bi.flt.group.label', { n: g + 1 })}>
              {group.map((f, i) => (
                <Chip
                t={t}
                  key={`g${g}-${i}`}
                  main={`${g}:${i}`}
                  label={dims(f)}
                  onEdit={() => setEditing({ kind: 'condition', at: { group: g, index: i }, draft: { dimension: f.dimension, op: f.op, values: f.values, currency: f.currency ?? currency, group: g } })}
                  onRemove={() => onChange(removeCondition(value, { group: g, index: i }))}
                />
              ))}
            </Row>
          </Stack>
        ))}
        {value.having.length > 0 && (
          <Stack gap={2}>
            <Text role="caption" tone="secondary">{t('bi.having.title')}</Text>
            <Row as="ul" gap={2} align="stretch" wrap aria-label={t('bi.having.title')}>
              {value.having.map((h, i) => (
                <Chip t={t} key={`h${i}`} main={`h${i}`} label={describeHaving(text, h)} onEdit={() => setEditing({ kind: 'having', index: i, spec: h })} onRemove={() => onChange(removeHaving(value, i))} />
              ))}
            </Row>
          </Stack>
        )}
      </div>

      {editing?.kind === 'condition' && (
        <ConditionEditor
          key={JSON.stringify(editing.at ?? 'new')}
          idPrefix={idPrefix}
          catalog={catalog}
          text={text}
          initial={editing.draft}
          editing={editing.at !== undefined}
          groups={value.anyOf.length}
          allowJoin={value.filters.length + value.anyOf.length > 0}
          refuse={refuse}
          dimensionIds={listed}
          onApply={(d, spec) => apply(editing, d, spec)}
          onCancel={() => setEditing(undefined)}
        />
      )}
      {editing?.kind === 'having' && havingMetrics !== undefined && (
        <HavingEditor
          key={String(editing.index ?? 'new')}
          idPrefix={idPrefix}
          catalog={catalog}
          text={text}
          metrics={havingMetrics}
          initial={editing.spec}
          editing={editing.index !== undefined}
          onApply={(spec) => {
            onChange(editing.index !== undefined ? replaceHaving(value, editing.index, spec) : addHaving(value, spec));
            setEditing(undefined);
          }}
          onCancel={() => setEditing(undefined)}
        />
      )}
    </Stack>
  );
}
